import React, { useEffect, useState, useMemo } from 'react';
import { X, Loader2, Search, AlertTriangle, Plus } from 'lucide-react';
import { EVENT_TYPES, EVENT_TYPE_LABELS, APPROVAL_STATUS, getEventsByRange, getWorkHours, jsDowToBackend, requestCorrection, createEvent, getMe } from '../services/api';
import {
  GEO_ORIGIN, GEO_REQUIRED_TITLE, captureGeoInfo, geoRequiredMessage, appendGeoToReason,
} from '../lib/geoInfo';
import { getBrowserPosition, resolveGeoMode } from '../services/browserLocation';
import {
  DEFAULT_TIME_ZONE, timeZoneForWorker, timeZoneLabel, dayKeyInZone, formatHmInZone,
  todayInZone, addDaysToDayKey, dowOfDayKey, zonedDateTimeToInstant,
} from '../lib/workerTime';
import { useToast } from './Toast';
import { FEEDBACK } from '../services/feedbackMessages';

// Todo el modal trabaja en la zona del TRABAJADOR (Canarias / península):
// el día elegido, las horas mostradas y las horas propuestas.
const todayStr = (tz = DEFAULT_TIME_ZONE) => todayInZone(tz);

// timestamp ISO -> "HH:MM" (hora de reloj del trabajador) para <input type="time">.
const isoToHM = (iso, tz = DEFAULT_TIME_ZONE) => (iso ? formatHmInZone(iso, tz) : '');

// "YYYY-MM-DD" + "HH:MM" (en la zona del trabajador) → ISO en UTC. Combina
// el día fijo del modal con la hora corregida por el usuario.
const dayAndHMtoIso = (day, hm, tz = DEFAULT_TIME_ZONE) => {
  if (!day || !hm) return null;
  const d = zonedDateTimeToInstant(day, hm, tz);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
};

// 'dd/mm/aaaa' de una clave de día (sin pasar por la zona del navegador).
const fmtDayKeyShort = (key) => (key ? `${key.slice(8, 10)}/${key.slice(5, 7)}/${key.slice(0, 4)}` : '');

// Convierte "HH:MM" (o "HH:MM:SS") a minutos desde medianoche.
const hmToMinutes = (hm) => {
  if (!hm) return null;
  const m = String(hm).match(/^(\d{1,2}):(\d{2})/);
  if (!m) return null;
  return Number(m[1]) * 60 + Number(m[2]);
};

// Devuelve los rangos [inicioMin, finMin] del slot del día (puede ser
// turno simple, partido o vacío). Slot null/undefined → [].
const slotRanges = (slot) => {
  if (!slot) return [];
  const out = [];
  const in1 = hmToMinutes(slot.in1);
  const out1 = hmToMinutes(slot.out1);
  const in2 = hmToMinutes(slot.in2);
  const out2 = hmToMinutes(slot.out2);
  if (Number.isFinite(in1) && Number.isFinite(out1) && out1 > in1) out.push([in1, out1]);
  if (Number.isFinite(in2) && Number.isFinite(out2) && out2 > in2) out.push([in2, out2]);
  return out;
};

const isHourInSlot = (hm, slot) => {
  const m = hmToMinutes(hm);
  if (m == null) return false;
  const ranges = slotRanges(slot);
  if (ranges.length === 0) return false;
  return ranges.some(([a, b]) => m >= a && m <= b);
};

// "HH:MM" sin segundos. Devuelve "" si la entrada es null o malformada.
const trim5 = (v) => v ? String(v).slice(0, 5) : '';

// Texto legible del rango horario del slot, p.ej. "07:00–15:00" o
// "09:00–13:00 · 16:00–20:00". Si el slot es null → "descanso".
const slotHelpText = (slot) => {
  if (!slot) return 'Día de descanso · no hay jornada definida';
  const parts = [];
  if (slot.in1 && slot.out1) parts.push(`${trim5(slot.in1)}–${trim5(slot.out1)}`);
  if (slot.in2 && slot.out2) parts.push(`${trim5(slot.in2)}–${trim5(slot.out2)}`);
  return parts.length ? parts.join(' · ') : 'sin franja horaria';
};

const fmtEventLabel = (e, tz = DEFAULT_TIME_ZONE) => {
  const label = EVENT_TYPE_LABELS[e.eventType] ?? `Tipo ${e.eventType}`;
  return `${label} · ${formatHmInZone(e.timestamp, tz)}`;
};

// Determina si las correcciones de este trabajador deben validarse contra
// los slots de su jornada. Solo "Validación de jornada" es estricta — los
// modos "Fichaje libre" y "Fichaje mixto" permiten proponer cualquier hora
// del día (decisión de producto: los trabajadores con horas extra activas
// pueden fichar antes/después de su jornada, así que la corrección tampoco
// debe encorsetarse al slot oficial).
//
// Tabla del modo (ver WorkerModal):
//   validation → cR=false, oA=false, eSH=false   → SLOT estricto
//   free       → cR=true,  oA=true,  eSH=false   → cualquier hora
//   mixed      → cR=false, oA=true,  eSH=true    → cualquier hora
const requiresSlotValidation = (worker) => {
  if (!worker) return true; // default conservador si no llega el dato
  // Si NO permite horas extra ni fichaje en diferido → es validation.
  return !worker.canRequestCheckins && !worker.overtimeAllowed;
};

const CorrectionRequestModal = ({ open, onClose, workerId, worker, onSubmitted }) => {
  // IMPORTANTE: TODOS los hooks van antes del `if (!open) return null`.
  // Saltarse este orden viola las reglas de hooks de React (Rules of Hooks)
  // y rompe la app en producción cuando el modal se abre y cierra varias veces.
  // Zona del trabajador que solicita (`worker` = user del AuthContext o fila
  // de trabajador; sin datos → Madrid).
  const tz = useMemo(
    () => timeZoneForWorker(worker),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [worker?.autonomousCommunity, worker?.community, worker?.province]
  );
  const tzHint = timeZoneLabel(tz);
  const [day, setDay] = useState(() => todayStr(tz));
  const [events, setEvents] = useState([]);
  const [workhoursByDow, setWorkhoursByDow] = useState(new Map());
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  // selections: { [eventId]: { eventType, hm } } — hm es solo "HH:MM",
  // el día queda fijo al `day` del modal y NO se puede modificar (decisión de
  // producto: la corrección es sobre el día seleccionado, no un cambio de día).
  const [selections, setSelections] = useState({});
  // missing: igual, solo "HH:MM" para los fichajes que faltan.
  const [missing, setMissing] = useState({
    entrada: { enabled: false, hm: '' },
    salida:  { enabled: false, hm: '' },
  });
  const [reason, setReason] = useState('');
  const [submitError, setSubmitError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const toast = useToast();

  useEffect(() => {
    if (!open) {
      setSelections({});
      setMissing({
        entrada: { enabled: false, hm: '' },
        salida:  { enabled: false, hm: '' },
      });
      setReason('');
      setSubmitError(null);
      setLoadError(null);
      setEvents([]);
      setWorkhoursByDow(new Map());
      setDay(todayStr(tz));
    }
  }, [open, tz]);

  // Carga la jornada del trabajador para poder validar que las horas
  // corregidas caen dentro del slot del día seleccionado.
  useEffect(() => {
    if (!open || !workerId) return;
    let cancelled = false;
    (async () => {
      try {
        const slots = await getWorkHours(workerId);
        if (cancelled) return;
        const map = new Map();
        for (const s of (Array.isArray(slots) ? slots : [])) {
          map.set(Number(s.dayOfWeek), s);
        }
        setWorkhoursByDow(map);
      } catch {
        if (!cancelled) setWorkhoursByDow(new Map());
      }
    })();
    return () => { cancelled = true; };
  }, [open, workerId]);

  useEffect(() => {
    if (!open || !workerId || !day) return;
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    setEvents([]);
    setSelections({});
    (async () => {
      try {
        // [día - 1, día + 1]: el día del trabajador empieza a las 22:00/23:00
        // UTC del anterior. Luego nos quedamos con los de `day` en su zona.
        const data = await getEventsByRange(workerId, addDaysToDayKey(day, -1), addDaysToDayKey(day, 1));
        if (cancelled) return;
        const list = Array.isArray(data) ? data : [];
        const dayEvents = list
          .filter((e) => dayKeyInZone(e.timestamp, tz) === day)
          .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
        setEvents(dayEvents);
      } catch (err) {
        if (!cancelled) setLoadError(err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, workerId, day, tz]);

  // Slot de jornada del día seleccionado (workhoursByDow lookup por dayOfWeek
  // del backend, que coincide con Date.getDay() de JS: Dom=0..Sáb=6).
  const daySlot = useMemo(() => {
    if (!day) return null;
    const dow = jsDowToBackend(dowOfDayKey(day));
    return workhoursByDow.get(dow) || null;
  }, [day, workhoursByDow]);

  // Pre-rellena las horas sugeridas de los "missing" cuando cambian los
  // eventos del día o el slot. Usamos como sugerencia in1/out2 (o in1/out1
  // si no hay turno tarde) para que la propuesta caiga dentro de jornada.
  // ESTE HOOK DEBE QUEDAR ANTES DEL `if (!open) return null`.
  useEffect(() => {
    if (!day) return;
    const hasIn = events.some((e) => e.eventType === EVENT_TYPES.CLOCK_IN);
    const hasOut = events.some((e) => e.eventType === EVENT_TYPES.CLOCK_OUT);
    const inSuggested = trim5(daySlot?.in1) || '08:00';
    const outSuggested = trim5(daySlot?.out2) || trim5(daySlot?.out1) || '18:00';
    setMissing((prev) => {
      const next = { ...prev };
      if (!hasIn && !prev.entrada.hm) {
        next.entrada = { ...prev.entrada, hm: inSuggested };
      }
      if (!hasOut && !prev.salida.hm) {
        next.salida = { ...prev.salida, hm: outSuggested };
      }
      return next;
    });
  }, [day, events, daySlot]);

  if (!open) return null;

  const toggleSelection = (event) => {
    setSelections((prev) => {
      const next = { ...prev };
      if (next[event.id]) {
        delete next[event.id];
      } else {
        next[event.id] = {
          eventType: event.eventType,
          hm: isoToHM(event.timestamp, tz),
        };
      }
      return next;
    });
  };

  const updateSelection = (eventId, patch) => {
    setSelections((prev) => ({
      ...prev,
      [eventId]: { ...prev[eventId], ...patch },
    }));
  };

  const selectedIds = Object.keys(selections);

  // Cálculos derivados de events/missing — NO son hooks, no afectan al orden.
  const hasEntrada = events.some((e) => e.eventType === EVENT_TYPES.CLOCK_IN);
  const hasSalida  = events.some((e) => e.eventType === EVENT_TYPES.CLOCK_OUT);
  const canAddEntrada = !hasEntrada;
  const canAddSalida  = !hasSalida && (hasEntrada || missing.entrada.enabled);
  const showAddSection = canAddEntrada || canAddSalida;

  const toggleMissing = (key) => {
    setMissing((prev) => ({ ...prev, [key]: { ...prev[key], enabled: !prev[key].enabled } }));
  };
  const updateMissing = (key, patch) => {
    setMissing((prev) => ({ ...prev, [key]: { ...prev[key], ...patch } }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError(null);
    const enabledMissing = ['entrada', 'salida'].filter((k) => missing[k].enabled);
    if (selectedIds.length === 0 && enabledMissing.length === 0) {
      setSubmitError('Selecciona al menos un evento a corregir o añade los fichajes que faltan.');
      return;
    }
    if (!reason.trim()) {
      setSubmitError('Indica el motivo de la corrección.');
      return;
    }
    const strictSlot = requiresSlotValidation(worker);
    // Validación de jornada: SOLO aplica si el trabajador es modo
    // "Validación de jornada". En fichaje libre/mixto puede corregir
    // cualquier hora del día (las extras se contabilizan después).
    // El caso "día de descanso sin slot" también se relaja en libre/mixto:
    // el trabajador puede registrar una jornada de cobertura, por ejemplo.
    if (strictSlot && !daySlot && (selectedIds.length > 0 || enabledMissing.length > 0)) {
      setSubmitError('Este día es descanso según tu jornada. Elige un día laborable.');
      return;
    }
    for (const id of selectedIds) {
      const sel = selections[id];
      if (!sel.hm) {
        setSubmitError('Hay correcciones sin hora.');
        return;
      }
      if (strictSlot && !isHourInSlot(sel.hm, daySlot)) {
        setSubmitError(`La hora ${sel.hm} está fuera de la jornada (${slotHelpText(daySlot)}).`);
        return;
      }
    }
    for (const k of enabledMissing) {
      if (!missing[k].hm) {
        setSubmitError(`Indica la hora de ${k} a añadir.`);
        return;
      }
      if (strictSlot && !isHourInSlot(missing[k].hm, daySlot)) {
        setSubmitError(`La hora ${missing[k].hm} de ${k} está fuera de la jornada (${slotHelpText(daySlot)}).`);
        return;
      }
    }
    setSubmitting(true);
    // Ubicación desde la que se REGISTRA la solicitud (decisión 2026-09-22).
    // Se captura UNA vez, antes de enviar nada: si la empresa la exige y no
    // se puede obtener, no se envía ninguna parte de la solicitud.
    //   · fichajes que faltaban → geoInfo con origen=correccion.
    //   · correcciones de fichajes existentes → POST /api/correction no
    //     tiene campo geoInfo: viaja al final del motivo ([geo:…]) y
    //     Aprobaciones la separa para pintarla (appendGeoToReason).
    const mode = await resolveGeoMode(worker, getMe);
    const infoToast = mode === 'off' ? null : toast.info('Obteniendo tu ubicación…', { duration: 0 });
    const cap = await captureGeoInfo({ mode, origin: GEO_ORIGIN.CORRECTION, getPosition: getBrowserPosition });
    if (infoToast) toast.dismiss(infoToast);
    if (cap.blocked) {
      const msg = geoRequiredMessage(cap.error?.message, { manual: true });
      toast.error(msg, undefined, { title: GEO_REQUIRED_TITLE, duration: 12000 });
      setSubmitError(msg);
      setSubmitting(false);
      return;
    }
    // Cuántas peticiones han llegado al servidor: si falla una a mitad, el
    // usuario tiene que saber que las anteriores SÍ se enviaron.
    let sent = 0;
    try {
      for (const id of selectedIds) {
        const sel = selections[id];
        await requestCorrection({
          workerId,
          pikachuId: Number(id),
          eventType: Number(sel.eventType),
          timestamp: dayAndHMtoIso(day, sel.hm, tz),
          reason: appendGeoToReason(reason.trim(), cap.geoInfo),
        });
        sent += 1;
      }
      const EVENT_FOR_MISSING = { entrada: EVENT_TYPES.CLOCK_IN, salida: EVENT_TYPES.CLOCK_OUT };
      for (const k of enabledMissing) {
        await createEvent({
          workerId,
          eventType: EVENT_FOR_MISSING[k],
          timestamp: dayAndHMtoIso(day, missing[k].hm, tz),
          geoInfo: cap.geoInfo,
          approvalStatus: APPROVAL_STATUS.PENDING,
        });
        sent += 1;
      }
      toast.success(FEEDBACK.correctionRequest({ dayKey: day, count: sent }));
      onSubmitted?.();
      onClose?.();
    } catch (err) {
      if (sent > 0) {
        // Parte ya está enviada: el padre debe refrescar igualmente.
        setSubmitError(FEEDBACK.correctionPartialFailure(sent, err));
        onSubmitted?.();
      } else {
        setSubmitError(err?.message || 'No se pudo enviar la solicitud.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const today = todayStr(tz);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40 backdrop-blur-sm p-4">
      <div className="bg-white w-full max-w-2xl rounded-2xl shadow-2xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <div>
            <h2 className="font-display text-lg font-bold text-ink">Solicitar corrección</h2>
            <p className="text-xs text-ink-muted mt-0.5">
              Marca los fichajes que quieres corregir y propone los valores correctos.
              Quedan pendientes hasta que un administrador los apruebe.
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-ink-muted hover:text-ink p-1 rounded-lg hover:bg-slate-100 transition-colors"
            aria-label="Cerrar"
          >
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4 overflow-y-auto">
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-ink-muted mb-2">Día</label>
            <input
              type="date"
              value={day}
              max={today}
              onChange={(e) => setDay(e.target.value)}
              className="w-full sm:w-64 px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:border-primary text-sm"
              required
            />
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-semibold uppercase tracking-wider text-ink-muted">Eventos del día</label>
              <span className="text-xs text-ink-muted">
                {tzHint && <>{tzHint} · </>}{events.length} registro{events.length === 1 ? '' : 's'}
              </span>
            </div>

            {loading ? (
              <div className="py-8 flex items-center justify-center gap-2 text-ink-muted">
                <Loader2 size={16} className="animate-spin text-primary" />
                <span className="text-sm">Cargando eventos…</span>
              </div>
            ) : loadError ? (
              <div className="text-xs text-danger bg-danger/5 border border-danger/20 rounded-lg px-3 py-2 flex items-start gap-2">
                <AlertTriangle size={14} className="mt-0.5 shrink-0" />
                <span>{loadError.message || 'No se pudieron cargar los eventos.'}</span>
              </div>
            ) : events.length === 0 ? (
              <div className="border border-dashed border-slate-200 rounded-lg py-6 text-center text-sm text-ink-muted flex flex-col items-center gap-2">
                <Search size={18} className="text-ink-muted/60" />
                Sin eventos registrados ese día.
              </div>
            ) : (
              <ul className="border border-slate-200 rounded-lg divide-y divide-slate-200">
                {events.map((ev) => {
                  const sel = selections[ev.id];
                  const checked = !!sel;
                  return (
                    <li key={ev.id} className="px-3 py-2.5">
                      <label className="flex items-start gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleSelection(ev)}
                          className="mt-1 accent-primary"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-semibold text-ink">{fmtEventLabel(ev, tz)}</div>
                          <div className="text-[11px] text-ink-muted">
                            {fmtDayKeyShort(dayKeyInZone(ev.timestamp, tz))}, {isoToHM(ev.timestamp, tz)}
                          </div>
                        </div>
                      </label>

                      {checked && (
                        <div className="mt-3 ml-7 grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <div>
                            <label className="block text-[11px] font-semibold uppercase tracking-wider text-ink-muted mb-1">Tipo correcto</label>
                            <select
                              value={sel.eventType}
                              onChange={(e) => updateSelection(ev.id, { eventType: Number(e.target.value) })}
                              className="w-full px-2 py-1.5 border border-slate-200 rounded-lg focus:outline-none focus:border-primary text-sm bg-white"
                            >
                              {Object.entries(EVENT_TYPE_LABELS).map(([val, label]) => (
                                <option key={val} value={val}>{label}</option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <label className="block text-[11px] font-semibold uppercase tracking-wider text-ink-muted mb-1">Hora correcta</label>
                            <input
                              type="time"
                              value={sel.hm}
                              onChange={(e) => updateSelection(ev.id, { hm: e.target.value })}
                              className="w-full px-2 py-1.5 border border-slate-200 rounded-lg focus:outline-none focus:border-primary text-sm"
                              required
                            />
                            <div className="text-[10px] text-ink-muted mt-1">
                              {requiresSlotValidation(worker) ? (
                                <>Jornada permitida: <span className="font-semibold">{slotHelpText(daySlot)}</span></>
                              ) : (
                                <>Cualquier hora del día (tu modo de fichaje no restringe el rango).</>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          {showAddSection && (
            <div className="border border-warning/30 bg-warning/5 rounded-lg p-3 space-y-3">
              <div className="flex items-start gap-2">
                <Plus size={14} className="text-warning shrink-0 mt-0.5" />
                <div className="flex-1 text-xs">
                  <div className="font-semibold text-ink">Añadir fichajes que faltan</div>
                  <div className="text-ink-muted mt-0.5">
                    {events.length === 0
                      ? 'Este día no tiene ningún fichaje. Si trabajaste, propón la entrada y la salida.'
                      : !hasEntrada
                        ? 'Falta la entrada de este día.'
                        : 'Falta la salida de este día.'}
                  </div>
                </div>
              </div>

              {canAddEntrada && (
                <div className="border-t border-warning/20 pt-3">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={missing.entrada.enabled}
                      onChange={() => toggleMissing('entrada')}
                      className="mt-1 accent-warning"
                    />
                    <div className="flex-1">
                      <div className="text-sm font-semibold text-ink">Añadir entrada</div>
                      {missing.entrada.enabled && (
                        <>
                          <input
                            type="time"
                            value={missing.entrada.hm}
                            onChange={(ev) => updateMissing('entrada', { hm: ev.target.value })}
                            className="mt-2 w-full sm:w-40 px-2 py-1.5 border border-slate-200 rounded-lg focus:outline-none focus:border-primary text-sm"
                            required
                          />
                          <div className="text-[10px] text-ink-muted mt-1">
                            Jornada permitida: <span className="font-semibold">{slotHelpText(daySlot)}</span>
                          </div>
                        </>
                      )}
                    </div>
                  </label>
                </div>
              )}

              {canAddSalida && (
                <div className={canAddEntrada ? 'border-t border-warning/20 pt-3' : ''}>
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={missing.salida.enabled}
                      onChange={() => toggleMissing('salida')}
                      className="mt-1 accent-warning"
                    />
                    <div className="flex-1">
                      <div className="text-sm font-semibold text-ink">Añadir salida</div>
                      {missing.salida.enabled && (
                        <>
                          <input
                            type="time"
                            value={missing.salida.hm}
                            onChange={(ev) => updateMissing('salida', { hm: ev.target.value })}
                            className="mt-2 w-full sm:w-40 px-2 py-1.5 border border-slate-200 rounded-lg focus:outline-none focus:border-primary text-sm"
                            required
                          />
                          <div className="text-[10px] text-ink-muted mt-1">
                            Jornada permitida: <span className="font-semibold">{slotHelpText(daySlot)}</span>
                          </div>
                        </>
                      )}
                    </div>
                  </label>
                </div>
              )}
            </div>
          )}

          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-ink-muted mb-2">Motivo</label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              placeholder="Ej. Olvidé fichar la salida porque se me acabó la batería del móvil."
              className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:border-primary text-sm resize-y"
              required
            />
            <div className="text-[11px] text-ink-muted mt-1">El motivo se aplica a todas las correcciones de esta solicitud.</div>
          </div>

          {submitError && (
            <div className="text-xs text-danger bg-danger/5 border border-danger/20 rounded-lg px-3 py-2">
              {submitError}
            </div>
          )}
        </form>

        <div className="flex justify-between items-center px-6 py-4 border-t border-slate-200">
          {(() => {
            const enabledMissingCount = (missing.entrada.enabled ? 1 : 0) + (missing.salida.enabled ? 1 : 0);
            const total = selectedIds.length + enabledMissingCount;
            return (
              <span className="text-xs text-ink-muted">
                {total === 0 ? 'Sin selección' : `${total} ${total === 1 ? 'cambio' : 'cambios'} a enviar`}
              </span>
            );
          })()}
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="px-4 py-2 rounded-lg text-sm font-semibold text-ink-muted hover:bg-slate-100 transition-colors disabled:opacity-60"
            >
              Cancelar
            </button>
            <button
              type="submit"
              onClick={handleSubmit}
              disabled={submitting || (selectedIds.length === 0 && !missing.entrada.enabled && !missing.salida.enabled)}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold bg-primary text-white hover:bg-primary-dark transition-colors disabled:opacity-60"
            >
              {submitting && <Loader2 size={14} className="animate-spin" />}
              Enviar solicitud
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CorrectionRequestModal;
